import { useQuery } from '@tanstack/react-query'
import { InfoIcon, Input, Select, Text, Toggle } from '@viapeople/components'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../../utils/trpc'

interface EmailSetupSectionProps {
	showReminderOptions?: boolean
	deadline?: string | null
}

const name = 'email'

const TabDescription = ({ children }: { children: React.ReactNode }) => (
	<div className='flex items-start gap-2 text-neutral-500'>
		<InfoIcon size={16} className='mt-0.5 shrink-0 text-neutral-400' />
		<Text variant='small'>{children}</Text>
	</div>
)

const tabDescriptions: Record<string, string> = {
	kickoff: 'Sent once when the step is activated, notifying participants that they can begin their work.',
	reminder: 'Sent periodically to remind participants of pending work until they complete the step.',
	lastCall: 'Sent once, a specific number of days before the deadline, as a final reminder to participants.',
	pastDue: 'Sent periodically after the deadline has passed, to follow up on overdue participants.',
}

export const EmailSetupSection = ({ showReminderOptions = false, deadline }: EmailSetupSectionProps) => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [activeTab, setActiveTab] = useState('kickoff')
	const {
		control,
		watch,
		formState: { errors },
	} = useFormContext()

	const emailErrors = (errors as Record<string, Record<string, unknown>>)?.[name] ?? {}

	const kickoffEnabled = watch(`${name}.kickoffEnabled`) ?? true
	const reminderEnabled = watch(`${name}.reminderEnabled`) ?? false
	const lastCallEnabled = watch(`${name}.lastCallEnabled`) ?? false
	const pastDueEnabled = watch(`${name}.pastDueEnabled`) ?? false

	const kickoffHasError = !!emailErrors?.kickoffTemplateId
	const reminderHasError = !!(
		emailErrors?.reminderTemplateId ||
		emailErrors?.reminderDays ||
		emailErrors?.reminderStartDays ||
		emailErrors?.maxReminderEmails
	)
	const lastCallHasError = !!(emailErrors?.lastCallTemplateId || emailErrors?.lastCallDaysBefore)
	const pastDueHasError = !!(
		emailErrors?.pastDueTemplateId ||
		emailErrors?.pastDueCadence ||
		emailErrors?.pastDueMaxEmails
	)

	const { data: emailTemplates } = useQuery({
		enabled: !!organizationId,
		...trpc.admin.emailTemplates.search.queryOptions({
			organizationId: organizationId as string,
			order: 'asc',
			sortBy: 'name',
			perPage: 99999,
		}),
	})

	const templateOptions =
		emailTemplates?.data.map((template) => ({
			value: template.id,
			label: template.name,
		})) || []

	const tabs = [
		{ id: 'kickoff', label: 'Kickoff', enabled: kickoffEnabled, hasError: kickoffHasError },
		...(showReminderOptions
			? [
					{ id: 'reminder', label: 'Reminder', enabled: reminderEnabled, hasError: reminderHasError },
					{ id: 'lastCall', label: 'Last Call', enabled: lastCallEnabled && deadline, hasError: lastCallHasError },
					{ id: 'pastDue', label: 'Past Due', enabled: pastDueEnabled && deadline, hasError: pastDueHasError },
				]
			: []),
	]

	return (
		<div className='flex flex-col border border-neutral-200 rounded-lg p-6 bg-white'>
			<Text variant='h6' weight='semibold'>
				Email Configuration
			</Text>

			<div className='flex border-b border-neutral-100 mb-4'>
				{tabs.map((tab) => {
					const isSelected = tab.id === activeTab
					return (
						<button
							key={tab.id}
							type='button'
							onClick={() => setActiveTab(tab.id)}
							className='group flex items-center gap-1.5 px-6 py-6 cursor-pointer relative text-neutral-800'>
							<Text
								variant='xsmall'
								weight={isSelected ? 'semibold' : 'regular'}
								className={tab.hasError ? 'text-red-500' : ''}>
								{tab.label}
							</Text>
							<span
								className={`w-2 h-2 rounded-full flex-shrink-0 ${
									tab.hasError ? 'bg-red-500' : tab.enabled ? 'bg-green-500' : 'border border-neutral-300'
								}`}
							/>
							<span
								className={`absolute bottom-0 left-0 w-full h-0.5 transition-colors ${
									isSelected ? 'bg-primary-600' : 'bg-transparent group-hover:bg-primary-600'
								}`}
							/>
						</button>
					)
				})}
			</div>

			{/* --- KICKOFF TAB --- */}
			<div className={activeTab !== 'kickoff' ? 'hidden' : 'flex flex-col gap-4'}>
				<TabDescription>{tabDescriptions.kickoff}</TabDescription>
				<Controller
					name={`${name}.kickoffEnabled`}
					defaultValue={true}
					control={control}
					render={({ field: { value, onChange, ...field } }) => (
						<Toggle
							{...field}
							label='Enable Kickoff Email'
							checked={value}
							onChange={(e) => onChange(e.target.checked)}
						/>
					)}
				/>
				{kickoffEnabled && (
					<Controller
						name={`${name}.kickoffTemplateId`}
						control={control}
						rules={{
							validate: (value, formValues) => {
								const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.kickoffEnabled ?? true
								if (enabled && !value) return 'Template is required when kickoff email is enabled'
								return true
							},
						}}
						render={({ field, fieldState: { error } }) => (
							<Select
								{...field}
								label='Kickoff Template'
								placeholder='Select template'
								options={templateOptions}
								variant={error ? 'error' : 'default'}
								hint={error?.message}
							/>
						)}
					/>
				)}
			</div>

			{/* --- REMINDER TAB --- */}
			{showReminderOptions && (
				<div className={activeTab !== 'reminder' ? 'hidden' : 'flex flex-col gap-4'}>
					<TabDescription>{tabDescriptions.reminder}</TabDescription>
					<Controller
						name={`${name}.reminderEnabled`}
						defaultValue={false}
						control={control}
						render={({ field: { value, onChange, ...field } }) => (
							<Toggle
								{...field}
								label='Enable Reminder Email'
								checked={value}
								onChange={(e) => onChange(e.target.checked)}
							/>
						)}
					/>
					{reminderEnabled && (
						<>
							<Controller
								name={`${name}.reminderTemplateId`}
								control={control}
								rules={{
									validate: (value, formValues) => {
										const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.reminderEnabled
										if (enabled && !value) return 'Template is required when reminder email is enabled'
										return true
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Select
										{...field}
										label='Reminder Template'
										placeholder='Select template'
										options={templateOptions}
										variant={error ? 'error' : 'default'}
										hint={error?.message}
									/>
								)}
							/>
							<Controller
								name={`${name}.reminderStartDays`}
								defaultValue={0}
								control={control}
								rules={{
									validate: (value, formValues) => {
										const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.reminderEnabled
										if (!enabled) return true
										if (value == null || Number.isNaN(Number(value))) return 'Must be a valid number'
										if (Number(value) < 0) return 'Cannot be negative'
										return true
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Input
										{...field}
										label='Start reminders after (days)'
										type='number'
										placeholder='e.g. 0'
										min={0}
										variant={error ? 'error' : 'default'}
										hint={
											error?.message ??
											'Days after cycle start before the first reminder is sent. Set to 0 to start immediately.'
										}
										onChange={(e) => field.onChange(e.target.valueAsNumber)}
									/>
								)}
							/>
							<div className='flex gap-4'>
								<Controller
									name={`${name}.reminderDays`}
									defaultValue={3}
									control={control}
									rules={{
										validate: (value, formValues) => {
											const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.reminderEnabled
											if (enabled && (value == null || Number.isNaN(Number(value)) || Number(value) < 1))
												return 'Cannot be less than 1'
											return true
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<Input
											{...field}
											label='Send every (days)'
											type='number'
											placeholder='e.g. 3'
											min={0}
											variant={error ? 'error' : 'default'}
											hint={error?.message}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
										/>
									)}
								/>
								<Controller
									name={`${name}.maxReminderEmails`}
									defaultValue={5}
									control={control}
									rules={{
										validate: (value, formValues) => {
											const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.reminderEnabled
											if (enabled && (value == null || Number.isNaN(Number(value)) || Number(value) < 0))
												return 'Cannot be negative'
											return true
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<Input
											{...field}
											label='Limit (Max emails)'
											type='number'
											placeholder='e.g. 5'
											min={0}
											variant={error ? 'error' : 'default'}
											hint={error?.message ?? 'Set to 0 to never stop sending.'}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
										/>
									)}
								/>
							</div>

							<Text variant='xsmall' className='text-neutral-400 text-center mt-2'>
								Emails will be sent at 9:00 AM ET.
							</Text>
						</>
					)}
				</div>
			)}

			{/* --- LAST CALL TAB --- */}
			{showReminderOptions && (
				<div className={activeTab !== 'lastCall' ? 'hidden' : 'flex flex-col gap-4'}>
					<TabDescription>
						{tabDescriptions.lastCall}
						{deadline && ` (Deadline: ${new Date(deadline).toLocaleDateString()})`}
					</TabDescription>

					<Controller
						name={`${name}.lastCallEnabled`}
						defaultValue={false}
						control={control}
						render={({ field: { value, onChange, ...field } }) => (
							<Toggle
								{...field}
								label='Enable Last Call Email'
								checked={deadline ? value : false}
								disabled={!deadline}
								description={
									!deadline ? 'A deadline must be set on this step before last call emails can be enabled.' : undefined
								}
								onChange={(e) => onChange(e.target.checked)}
							/>
						)}
					/>
					{lastCallEnabled && deadline && (
						<>
							<Controller
								name={`${name}.lastCallTemplateId`}
								control={control}
								rules={{
									validate: (value, formValues) => {
										const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.lastCallEnabled
										if (enabled && !value) return 'Template is required when last call email is enabled'
										return true
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Select
										{...field}
										label='Last Call Template'
										placeholder='Select template'
										options={templateOptions}
										variant={error ? 'error' : 'default'}
										hint={error?.message}
									/>
								)}
							/>
							<Controller
								name={`${name}.lastCallDaysBefore`}
								defaultValue={1}
								control={control}
								rules={{
									validate: (value, formValues) => {
										const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.lastCallEnabled
										if (enabled && (value == null || Number.isNaN(Number(value)) || Number(value) < 0))
											return 'Must be at least 0 when last call email is enabled'
										return true
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Input
										{...field}
										label='Days before deadline'
										type='number'
										placeholder='e.g. 1'
										min={0}
										variant={error ? 'error' : 'default'}
										hint={error?.message ?? 'Set to 0 to send on the day of the deadline.'}
										onChange={(e) => field.onChange(e.target.valueAsNumber)}
									/>
								)}
							/>

							<Text variant='xsmall' className='text-neutral-400 text-center mt-2'>
								Emails will be sent at 9:00 AM ET.
							</Text>
						</>
					)}
				</div>
			)}

			{/* --- PAST DUE TAB --- */}
			{showReminderOptions && (
				<div className={activeTab !== 'pastDue' ? 'hidden' : 'flex flex-col gap-4'}>
					<TabDescription>{tabDescriptions.pastDue}</TabDescription>
					<Controller
						name={`${name}.pastDueEnabled`}
						defaultValue={false}
						control={control}
						render={({ field: { value, onChange, ...field } }) => (
							<Toggle
								{...field}
								label='Enable Past Due Email'
								checked={deadline ? value : false}
								disabled={!deadline}
								description={
									!deadline ? 'A deadline must be set on this step before past due emails can be enabled.' : undefined
								}
								onChange={(e) => onChange(e.target.checked)}
							/>
						)}
					/>
					{pastDueEnabled && deadline && (
						<>
							<Controller
								name={`${name}.pastDueTemplateId`}
								control={control}
								rules={{
									validate: (value, formValues) => {
										const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.pastDueEnabled
										if (enabled && !value) return 'Template is required when past due email is enabled'
										return true
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Select
										{...field}
										label='Past Due Template'
										placeholder='Select template'
										options={templateOptions}
										variant={error ? 'error' : 'default'}
										hint={error?.message}
									/>
								)}
							/>
							<div className='flex gap-4'>
								<Controller
									name={`${name}.pastDueCadence`}
									defaultValue={2}
									control={control}
									rules={{
										validate: (value, formValues) => {
											const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.pastDueEnabled
											if (enabled && (value == null || Number.isNaN(Number(value)) || Number(value) < 1))
												return 'Must be at least 1'
											return true
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<Input
											{...field}
											label='Send every (days)'
											type='number'
											placeholder='e.g. 2'
											min={1}
											variant={error ? 'error' : 'default'}
											hint={error?.message}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
										/>
									)}
								/>
								<Controller
									name={`${name}.pastDueMaxEmails`}
									defaultValue={3}
									control={control}
									rules={{
										validate: (value, formValues) => {
											const enabled = (formValues as Record<string, Record<string, unknown>>)?.email?.pastDueEnabled
											if (enabled && (value == null || Number.isNaN(Number(value)) || Number(value) < 0))
												return 'Cannot be negative'
											return true
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<Input
											{...field}
											label='Limit (Max emails)'
											type='number'
											placeholder='e.g. 3'
											min={0}
											variant={error ? 'error' : 'default'}
											hint={error?.message ?? 'Set to 0 to never stop sending.'}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
										/>
									)}
								/>
							</div>

							<Text variant='xsmall' className='text-neutral-400 text-center mt-2'>
								Emails will be sent at 9:00 AM ET.
							</Text>
						</>
					)}
				</div>
			)}
		</div>
	)
}
